import React from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {css} from 'emotion';
import Text from '@amzn/meridian/text';
import ConstraintDetailsTable from './ConstraintDetailsTable';
import {GetConstraintResponse, GetConstraintResponseRow} from '../../../common/apis/models/getConstraintResponse';
import {GRANULARITY} from '../ConstraintDetailsPage';
import {getLocalizedAPIsStartDate, getLocalizedDateTimeStrings} from '../../../common/utils/dates';

const TOTAL_DAYS = 91; // 13 weeks

interface IProps {
  history: RouteComponentProps['history'];
  mainConstraintDetails: GetConstraintResponse | void;
  constraintDetails: GetConstraintResponse;
  constraintId: string;
  businessType: string;
  region: string;
  user: string;
  enableEdits: boolean;
  country?: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}

const labelStyle = css`
  display: inline-block;
  padding: 4px 10px;
  margin-bottom: 8px;
  background-color: #e8f4fd;
  border: 1px solid #b8daff;
  border-radius: 4px;
  font-weight: 600;
`;

/**
 * Expand PIT weekly values into daily values with forward-fill.
 * Each day gets the last known per-slot value (propagated until a new value appears).
 */
function expandPITToDaily(constraintDetails: GetConstraintResponse): GetConstraintResponse {
  const currDate = new Date();
  const startTime = getLocalizedAPIsStartDate(currDate);
  const startDate = new Date(startTime);

  const expandedValuesData = (constraintDetails.valuesData || []).map((row) => {
    const expandedRow: GetConstraintResponseRow = {
      siteId: row.siteId,
      siteName: row.siteName,
    };

    let lastValue: string | undefined;

    for (let i = 0; i < TOTAL_DAYS; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const dayKey = getLocalizedDateTimeStrings(d).date;

      if (row[dayKey] !== undefined && row[dayKey] !== null && row[dayKey] !== '') {
        lastValue = row[dayKey];
      } else {
        const weekStart = new Date(d);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekKey = getLocalizedDateTimeStrings(weekStart).date;
        if (row[weekKey] !== undefined && row[weekKey] !== null && row[weekKey] !== '') {
          lastValue = row[weekKey];
        }
      }

      if (lastValue !== undefined) {
        expandedRow[dayKey] = lastValue;
      }
    }

    return expandedRow;
  });

  return {
    ...constraintDetails,
    inputGranularity: GRANULARITY.Daily,
    valuesData: expandedValuesData,
  };
}

const PITConstraintDetailsView = ({
  history,
  mainConstraintDetails,
  constraintDetails,
  constraintId,
  businessType,
  region,
  user,
  enableEdits,
  country,
  setQuery,
}: IProps) => {
  const displayDetails = expandPITToDaily(constraintDetails);
  const displayMainDetails = mainConstraintDetails
    ? expandPITToDaily(mainConstraintDetails)
    : undefined;

  return (
    <div>
      <div className={labelStyle}>
        <Text type="b200">Per Slot</Text>
      </div>
      <ConstraintDetailsTable
        history={history}
        mainConstraintDetails={displayMainDetails}
        constraintDetails={displayDetails}
        enableEdits={enableEdits}
        constraintId={constraintId}
        businessType={businessType}
        region={region}
        user={user}
        country={country}
        setQuery={setQuery}
        maxColumns={91}
      />
    </div>
  );
};

export default PITConstraintDetailsView;
